import { Box, CircularProgress, Typography, useTheme } from "@mui/material";
import { Assignment, CheckCircle, Rocket } from "@mui/icons-material";
import Lottie from "lottie-react";
import React, { useContext, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from "react-router-dom";
import animationData from '../../assets/aiden boat.json';
import { Context as AppContext } from "../../context/AppContext";
import { MainLayout } from "../../layouts/main-layout/MainLayout";
import ListOfInterns from "./ListOfInterns";
import './mentorHomepage.css';
const Applicants = () => {
    const theme = useTheme();
    const {
        state: { currentUser, applicants },
        getApplicants
    } = useContext(AppContext);
    const navigate = useNavigate();

    const isLoading = useMemo(() => applicants === null, [applicants]);
    const hasApplicants = useMemo(() => applicants?.length > 0, [applicants]);

    const fetchApplicants = useCallback(async () => {
        if (currentUser?.uid) {
            try {
                await getApplicants(currentUser.uid);
            } catch (error) {
                console.error('Failed to fetch applicants:', error);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.uid]);

    useEffect(() => {
        fetchApplicants();
    }, [fetchApplicants]);

    return (
        <MainLayout>
            <Box sx={{
                minHeight: '100vh',
                bgcolor: '#fafbfc',
                fontFamily: 'Poppins, system-ui, -apple-system, sans-serif'
            }}>
                <Box sx={{
                    maxWidth: '1200px',
                    mx: 'auto',
                    px: { xs: 2, md: 3 },
                    py: { xs: 2, md: 3 }
                }}>
                    {/* Header */}
                    <Box sx={{ mb: 3 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                fontWeight: 600,
                                color: '#1a1a1a',
                                mb: 1,
                                letterSpacing: '-0.02em'
                            }}
                        >
                            Applicants
                            <Box component="span" sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 700
                            }}>
                                {hasApplicants && ` (${applicants.length})`}
                            </Box>
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: '#64748b',
                                fontSize: '1rem',
                                maxWidth: '600px'
                            }}
                        >
                            Review and manage student applications for your mentorship program.
                        </Typography>
                    </Box>

                    {/* Content */}
                    {isLoading ? (
                        <Box sx={{
                            bgcolor: 'white',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                            p: { xs: 3, md: 4 },
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 3,
                            minHeight: '300px',
                            justifyContent: 'center'
                        }}>
                            <CircularProgress
                                size={48}
                                sx={{ color: theme.palette.primary.main }}
                            />
                            <Typography
                                sx={{
                                    color: '#64748b',
                                    fontSize: '1.125rem',
                                    fontWeight: 500
                                }}
                            >
                                Loading applicants...
                            </Typography>
                        </Box>
                    ) : hasApplicants ? (
                        <Box sx={{
                            bgcolor: 'white',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                            p: { xs: 3, md: 4 },
                            transition: 'box-shadow 0.2s ease-in-out',
                            '&:hover': {
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                            }
                        }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 3,
                                flexWrap: 'wrap',
                                gap: 2
                            }}>
                                <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 600,
                                        color: '#1e293b',
                                        fontSize: '1.25rem'
                                    }}
                                >
                                    Application Review
                                </Typography>
                                <Box sx={{
                                    px: 2,
                                    py: 0.5,
                                    bgcolor: '#f0f9ff',
                                    color: '#0c4a6e',
                                    borderRadius: '20px',
                                    fontSize: '0.875rem',
                                    fontWeight: 500
                                }}>
                                    {applicants.length} {applicants.length === 1 ? 'Application' : 'Applications'}
                                </Box>
                            </Box>
                            <ListOfInterns students={applicants} />
                        </Box>
                    ) : (
                        <Box sx={{
                            bgcolor: 'white',
                            borderRadius: '20px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                            overflow: 'hidden',
                            transition: 'box-shadow 0.2s ease-in-out',
                            '&:hover': {
                                boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                            }
                        }}>
                            <Box sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
                                <Box sx={{ mb: 4 }}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            mb: 2,
                                            fontSize: { xs: '1.25rem', md: '1.5rem' }
                                        }}
                                    >
                                        No Applications Yet
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#64748b',
                                            fontSize: '1rem',
                                            maxWidth: '500px',
                                            mx: 'auto',
                                            lineHeight: 1.6
                                        }}
                                    >
                                        Students haven't applied to your mentorship program yet. Applications will appear here when they're submitted.
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                                    <Lottie
                                        animationData={animationData}
                                        loop={true}
                                        style={{ height: '180px', width: '180px' }}
                                    />
                                </Box>

                                {/* Feature Cards */}
                                <Box sx={{
                                    pt: 3,
                                    borderTop: '1px solid #f1f5f9',
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                                    gap: 3
                                }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            bgcolor: '#f0f9ff',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2
                                        }}>
                                            <Assignment sx={{ fontSize: '1.5rem', color: '#3b82f6' }} />
                                        </Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 0.5
                                            }}
                                        >
                                            Review Applications
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748b' }}
                                        >
                                            Evaluate student profiles
                                        </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            bgcolor: '#f0fdf4',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2
                                        }}>
                                            <CheckCircle sx={{ fontSize: '1.5rem', color: '#10b981' }} />
                                        </Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 0.5
                                            }}
                                        >
                                            Accept Students
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748b' }}
                                        >
                                            Build your mentee group
                                        </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            bgcolor: '#fefce8',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2
                                        }}>
                                            <Rocket sx={{ fontSize: '1.5rem', color: '#f59e0b' }} />
                                        </Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 0.5
                                            }}
                                        >
                                            Start Mentoring
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748b' }}
                                        >
                                            Guide their journey
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        </MainLayout>
    );
};

export default Applicants;
